import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AUTOMATION_PATH, HOME_PATH, PROJECTS_PATH, WEBSITES_PATH } from '../routes';
import './Navigation.css';

// Set on published builds; in dev the switcher stays an in-page toggle.
const FORCED_LOCALE = process.env.REACT_APP_LOCALE;
const SITE_PREFIX = { pl: '', en: '/en' };

// Section anchors live on the home page; `to` entries are pages of their own;
// `children` open as a submenu under their parent.
const NAV_LINKS = [
  { id: 'projects', key: 'navigation.projects' },
  {
    id: 'services',
    key: 'navigation.services',
    children: [
      { to: WEBSITES_PATH, key: 'servicePages.websites.eyebrow' },
      { to: AUTOMATION_PATH, key: 'servicePages.automation.eyebrow' }
    ]
  },
  { id: 'about', key: 'navigation.about' },
  { id: 'faq', key: 'navigation.faq' },
  { to: PROJECTS_PATH, key: 'navigation.allProjects' },
  { id: 'contact', key: 'navigation.contact' }
];

function Navigation() {
  const { t, i18n } = useTranslation();
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const onHome = pathname === HOME_PATH;

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, [open]);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // On the home page anchors scroll in place; elsewhere they lead back home
  // and the router's ScrollManager picks up the hash.
  const sectionLink = (id, children, className) =>
    onHome ? (
      <a href={`#${id}`} className={className} onClick={scrollTo(id)}>{children}</a>
    ) : (
      <Link to={`${HOME_PATH}#${id}`} className={className} onClick={() => setOpen(false)}>
        {children}
      </Link>
    );

  return (
    <nav className="nav">
      {sectionLink('top', 'deos', 'brand')}

      <button
        className={`nav-toggle ${open ? 'is-open' : ''}`}
        aria-label="Toggle menu"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
      >
        <span /><span /><span />
      </button>

      <div className={`nav-right ${open ? 'is-open' : ''}`}>
        <ul className="nav-links">
          {NAV_LINKS.map(({ id, to, key, children }) => (
            <li key={id || to} className={children ? 'has-sub' : undefined}>
              {to ? (
                <NavLink to={to} onClick={() => setOpen(false)}>{t(key)}</NavLink>
              ) : (
                sectionLink(id, t(key))
              )}
              {children && (
                <ul className="nav-sub">
                  {children.map(child => (
                    <li key={child.to}>
                      <NavLink to={child.to} onClick={() => setOpen(false)}>{t(child.key)}</NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        {FORCED_LOCALE ? (
          // Each language is its own build, so switching is a full page load
          // to the same path on the other site.
          <div className="lang-switcher">
            <a
              href={`${SITE_PREFIX.pl}${pathname}${search}`}
              className={FORCED_LOCALE === 'pl' ? 'active' : ''}
            >PL</a>
            <span>·</span>
            <a
              href={`${SITE_PREFIX.en}${pathname}${search}`}
              className={FORCED_LOCALE === 'en' ? 'active' : ''}
            >EN</a>
          </div>
        ) : (
          <div className="lang-switcher">
            <button
              type="button"
              onClick={() => i18n.changeLanguage('pl')}
              className={i18n.language?.startsWith('pl') ? 'active' : ''}
            >PL</button>
            <span>·</span>
            <button
              type="button"
              onClick={() => i18n.changeLanguage('en')}
              className={i18n.language?.startsWith('en') ? 'active' : ''}
            >EN</button>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navigation;
